import IoBaseApiSlice, { ErrorResponse } from "./ioBaseApiSlice";
import { pseudoLogout } from "../sspSlice";

export const INVALID_VERIFICATION_CODE = "invalid_otp";

interface ResetPasswordArgs {
  email: string;
}

interface SetPasswordArgs {
  username: string;
  otp: string;
  newPassword: string;
}

interface ChangePasswordArgs {
  new_password: string;
}

export interface GetProfileContactResponse {
  email: string;
  phone: string;
  mobile: string;
}

export interface GetProfileInfoResponse {
  donor_id: string;
  salutation: string;
  firstname: string;
  lastname: string;
  birthdate: string | null;
  sex: string | null;
  title: string | null;
  avatar?: {
    last_change?: string;
  };
}

export interface GetStatusInfoResponse {
  status: string;
  consentform_signed: boolean;
  sample_typed: string | null;
  swabkit_ordered: string | null;
  sample_provided: string | null;
  sample_laboratory_entry: string | null;
  sample_returned: string | null;
}

export interface GetUserAddressResponse {
  street: string;
  houseno: string;
  zipcode: string;
  city: string;
  apartment: string | null;
  co: string | null;
  country: string | null;
}

interface LoginArgs {
  user: string;
  password: string;
}

interface LoginResponse {
  response: number;
}

interface LogoutResponse {
  url: string;
}

interface SetIdentityArgs {
  registration_data: {
    code: string;
    identifier: string;
  };
}

interface SubmitSspUserArgs {
  contact_data: {
    email: string;
  };
  registration_data: {
    firstname: string;
    lastname: string;
    password: string;
  };
}

export interface SubmitSspUserResponse {
  identifier: string;
}

export const IoSspApiSlice = IoBaseApiSlice.injectEndpoints({
  endpoints: (build) => ({
    changePassword: build.mutation<void, ChangePasswordArgs>({
      invalidatesTags: ["SSP"],
      query: (data) => ({
        apiRequirements: ["session", "csrfToken"],
        data,
        method: "post",
        url: `${process.env.GATSBY_IO_HOST}/ciam/password`,
      }),
    }),
    getProfileContact: build.query<GetProfileContactResponse, void>({
      keepUnusedDataFor: 60,
      providesTags: ["SSP"],
      query: () => ({
        apiRequirements: ["session"],
        url: `${process.env.GATSBY_IO_HOST}/ciu/urn:dkmsio:vault:model::20210719:contact`,
      }),
      transformResponse: ({ payload }) => payload,
    }),
    getProfileInfo: build.query<GetProfileInfoResponse, void>({
      keepUnusedDataFor: 60,
      providesTags: ["SSP"],
      query: () => ({
        apiRequirements: ["session"],
        url: `${process.env.GATSBY_IO_HOST}/ciu/urn:dkmsio:vault:model::20210719:person`,
      }),
      transformResponse: ({ payload }) => payload,
    }),
    getStatusInfo: build.query<GetStatusInfoResponse, void>({
      keepUnusedDataFor: 60,
      providesTags: ["SSP"],
      query: () => ({
        apiRequirements: ["session"],
        url: `${process.env.GATSBY_IO_HOST}/reg/urn:dkmsio:vault:model::20210719:typingsample`,
      }),
      transformResponse: ({ payload }) => payload,
    }),
    getUserAddress: build.query<GetUserAddressResponse, void>({
      keepUnusedDataFor: 60,
      providesTags: ["SSP"],
      query: () => ({
        apiRequirements: ["session"],
        url: `${process.env.GATSBY_IO_HOST}/ciu/urn:dkmsio:vault:model::20210719:address/primary`,
      }),
      transformResponse: ({ payload }) => payload,
    }),
    login: build.mutation<LoginResponse, LoginArgs>({
      invalidatesTags: ["SSP"],
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          if (data.response === 200 || data.response === 202) {
            dispatch(pseudoLogout(false));

            window.location.href = `${process.env.GATSBY_SITE_URL}${process.env.GATSBY_SSP_URL_PREFIX || ""}`;
          }
        } catch {
          // Do nothing
        }
      },
      query: ({ user, password }) => ({
        apiRequirements: ["session", "csrfToken"],
        data: {
          payload: {
            login_data: {
              password,
              user,
            },
          },
        },
        method: "post",
        url: `${process.env.GATSBY_IO_HOST}/ciam/${process.env.GATSBY_IO_CIAM_URN}/loginapi`,
      }),
    }),
    logout: build.mutation<LogoutResponse, void>({
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(pseudoLogout(false));

          window.location.href = data.url;
        } catch {
          // Do nothing
        }
      },
      query: () => ({
        apiRequirements: ["session"],
        url: `${process.env.GATSBY_IO_HOST}/ciam/${process.env.GATSBY_IO_CIAM_URN}/logout`,
      }),
      transformResponse: ({ payload }) => payload,
    }),
    resetPassword: build.mutation<void, ResetPasswordArgs>({
      invalidatesTags: ["SSP"],
      query: ({
        email = "",
      }) => ({
        apiRequirements: ["session", "csrfToken"],
        data: {
          payload: {
            email,
          },
        },
        method: "post",
        url: `${process.env.GATSBY_IO_HOST}/ciam/${process.env.GATSBY_IO_URN_PASSWORD_REQUEST}`,
      }),
      transformResponse: ({ payload }) => payload,
    }),
    setIdentity: build.mutation<void, SetIdentityArgs>({
      query: (data) => ({
        apiRequirements: ["session", "csrfToken"],
        data: {
          payload: data,
        },
        method: "post",
        url: `${process.env.GATSBY_IO_HOST}/reg/urn:dkmsio:registration:sspidentity:DKMS_DE:20201130/identityset`,
      }),
      transformErrorResponse: (baseQueryReturnValue: ErrorResponse) => {
        if (baseQueryReturnValue.status === 400
          && baseQueryReturnValue.data?.detail?.error === INVALID_VERIFICATION_CODE) {
          return INVALID_VERIFICATION_CODE;
        }

        return baseQueryReturnValue;
      },
    }),
    setPassword: build.mutation<void, SetPasswordArgs>({
      invalidatesTags: ["SSP"],
      query: ({
        username = "",
        otp = "",
        newPassword = "",
      }) => ({
        apiRequirements: ["session", "csrfToken"],
        data: {
          payload: {
            new_password: newPassword,
            otp,
            username,
          },
        },
        method: "post",
        url: `${process.env.GATSBY_IO_HOST}/ciam/${process.env.GATSBY_IO_URN_SET_PASSWORD}`,
      }),
      transformResponse: ({ payload }) => payload,
    }),
    submitSspUser: build.mutation<SubmitSspUserResponse, SubmitSspUserArgs>({
      query: (data) => ({
        apiRequirements: ["session", "csrfToken"],
        data: {
          payload: data,
        },
        method: "post",
        url: `${process.env.GATSBY_IO_HOST}/reg/urn:dkmsio:registration:ssp:DKMS_DE:20201130/identityrequest`,
      }),
      transformResponse: ({ payload }) => payload,
    }),
  }),
  overrideExisting: false,
});

export const {
  useSubmitSspUserMutation,
  useSetIdentityMutation,
  useChangePasswordMutation,
  useGetProfileContactQuery,
  useGetProfileInfoQuery,
  useGetStatusInfoQuery,
  useGetUserAddressQuery,
  useLoginMutation,
  useLogoutMutation,
  useResetPasswordMutation,
  useSetPasswordMutation,
} = IoSspApiSlice;
